import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'
import {
  ButtonBase,
  Fade,
  InputBase,
  MenuItem,
  useMediaQuery,
} from '@material-ui/core'
import { Actions, DownshiftProps, PropGetters } from 'downshift'
import { HandleSearch, SearchOptions } from './useHeaderAutoSuggest'
import SearchIcon from '@icons/SearchIcon'
import messages from '@utils/messages'
import BackArrowIcon from '@src/icons/BackArrowIcon'
import XIcon from '@src/icons/XIcon'
import clsx from 'clsx'
import hasTouch from '@src/styles/utils/hasTouch'
import LiquidSelect from '@src/components/LiquidSelect'
import { SearchOption, useSearchOptions } from '@src/utils/searchUtils'
import {
  getSearchFocusEventValue,
  sendSearchInteractionEvent,
} from '@src/utils/analytics'
import {
  HeaderSearchAddToCart,
  HeaderSearchAddToCartProps,
} from './HeaderSearchAddToCart'
import { useCurrentUser } from '@utils/useCurrentUser'

const useStyles = makeStyles((theme: Theme) => ({
  autoSuggestInputWrapper: {
    display: 'flex',
    padding: theme.spacing(2.5, 4),
    border: 'none',
    borderRadius: '5px',
    [theme.breakpoints.up('md')]: {
      border: `1px solid ${theme.palette.grey[500]}`,
      height: 48,
      padding: 0,
    },
  },
  searchInputRoot: {
    borderRadius: 5,
    paddingLeft: theme.spacing(3),
    border: `1px solid ${theme.palette.grey[500]}`,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      border: 'none',
    },
  },
  searchInput: {
    order: 2,
    height: '46px',
    paddingTop: '0',
    paddingBottom: '0',
    '&::placeholder': {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.grey[500],
      opacity: '1',
    },
    [theme.breakpoints.up('md')]: {
      '&::placeholder': {
        fontSize: theme.typography.pxToRem(14),
        opacity: '0.75 !important',
        color: 'unset',
      },
    },
    '&:focus::placeholder': {
      // Visually remove placeholder text when input is focused.
      color: 'transparent',
    },
  },
  searchButton: {
    order: 4,
    flexShrink: 0,
    height: '70%',
    width: theme.spacing(11),
    borderLeft: `1px solid ${theme.palette.grey[400]}`,
    marginLeft: theme.spacing(4),
    color: theme.palette.brand.main,
    [theme.breakpoints.up('md')]: {
      height: '100%',
      width: theme.spacing(14),
      fontSize: theme.typography.pxToRem(18),
      color: theme.palette.common.white,
      backgroundColor: theme.palette.brand.main,
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
      border: `1px solid ${theme.palette.brand.main}`,
      boxSizing: 'content-box',
      right: '-1px',
      marginLeft: theme.spacing(3),
    },
  },
  backButton: {
    order: 1,
    height: '100%',
    marginRight: 12,
    display: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  hideBackButton: {
    display: 'none',
  },
  scanButton: { display: 'inline-flex', marginLeft: 12, order: 4 },
  addToCartGroup: {
    flexShrink: 0,
    order: 3,
    marginLeft: theme.spacing(3),
  },
  clearButton: {
    display: 'none',
    order: 3,
    height: '100%',
    marginLeft: 12,
    fontSize: theme.typography.pxToRem(10),
  },
  showClear: {
    display: 'inline-flex',
  },
  searchTypeSelection: {
    '& .MuiSelect-root': {
      height: '38px',
      lineHeight: '30px',
      boxShadow: 'none',
      position: 'relative',
      padding: '4px 36px 4px 12px',
      [theme.breakpoints.up('md')]: {
        height: '46px',
        lineHeight: '36px',
      },

      '&:after': {
        content: "''",
        position: 'absolute',
        display: 'block',
        height: '24px',
        width: '1px',
        right: 0,
        bottom: '7px',
        background: theme.palette.grey[500],
        [theme.breakpoints.up('md')]: {
          bottom: '11px',
        },
      },
      '&:hover': {
        boxShadow: 'none',
      },
      '&:focus-visible': {
        outline: 'revert',
      },
    },
    '& .MuiSvgIcon-root': {
      top: '16px',
      [theme.breakpoints.up('md')]: {
        top: '20px',
      },
    },
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
}))

interface HeaderSearchInputProps {
  getInputProps: PropGetters<SearchOptions>['getInputProps']
  openMenu: Actions<SearchOptions>['openMenu']
  closeMenu: Actions<SearchOptions>['closeMenu']
  isOpen: DownshiftProps<SearchOptions>['isOpen']
  handleSearch: HandleSearch
  inputEl: React.RefObject<HTMLInputElement>
  handleClearSelection: Actions<SearchOptions>['clearSelection']
  inputValue: DownshiftProps<SearchOptions>['inputValue']
  setFocusType: (type: any) => void
  focusType: string
  orgId: string | null | undefined
  id: string
  headerSearchAddToCartProps: HeaderSearchAddToCartProps
}

const HeaderSearchInput: React.FC<HeaderSearchInputProps> = ({
  getInputProps,
  openMenu,
  closeMenu,
  isOpen,
  handleSearch,
  inputEl,
  handleClearSelection,
  inputValue,
  setFocusType,
  focusType,
  orgId,
  id,
  headerSearchAddToCartProps,
}) => {
  const { currentUser } = useCurrentUser()
  const classes = useStyles()
  const theme = useTheme()
  const { formatMessage } = useIntl()
  const { onBlur } = getInputProps()
  const backButtonClasses = clsx({
    [classes.backButton]: true,
    [classes.hideBackButton]: !isOpen,
  })
  const options: SearchOption[] = useSearchOptions()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true
  const [dropdownLabel, setDropdownLabel] = useState('products')
  const searchValue = inputEl.current?.defaultValue

  return (
    <div className={classes.autoSuggestInputWrapper}>
      {!orgId && (
        <LiquidSelect
          onChange={(e) => {
            sendSearchInteractionEvent({
              action: 'search scope change',
              component: 'drop-down menu',
              elementType: 'button',
              elementText: getSearchFocusEventValue(
                e.target.value
              ).toLowerCase(),
              detail: dropdownLabel,
            })
            setDropdownLabel(getSearchFocusEventValue(e.target.value))
            setFocusType(e.target.value)
          }}
          size="small"
          value={focusType}
          fullWidth={false}
          className={classes.searchTypeSelection}
        >
          {options.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </LiquidSelect>
      )}
      <InputBase
        {...getInputProps()}
        data-testid="primary-search-input"
        onFocus={() => openMenu()}
        onBlur={(...args: [event: React.FocusEvent<HTMLInputElement>]) => {
          if (!hasTouch) {
            onBlur && onBlur(...args)
          }
        }}
        inputRef={inputEl}
        placeholder={formatMessage(messages.HEADER_SEARCH_PLACEHOLDER)}
        classes={{
          root: classes.searchInputRoot,
          input: classes.searchInput,
        }}
        onKeyUp={(event) => {
          if (event.key === 'Enter' && isOpen) {
            closeMenu()
            handleSearch({ focusType, searchValue, dropdownLabel })
          }
        }}
        inputProps={{
          spellCheck: false,
        }}
        endAdornment={
          <>
            <ButtonBase
              className={backButtonClasses}
              onClick={() => {
                closeMenu()
              }}
            >
              <BackArrowIcon fontSize="inherit" />
            </ButtonBase>
            {headerSearchAddToCartProps.material &&
              !isDesktop &&
              (!headerSearchAddToCartProps.material.isMarketplace ||
                currentUser?.metadata.isMarketplaceCartEnabled) && (
                <Fade in>
                  <div className={classes.addToCartGroup}>
                    <HeaderSearchAddToCart {...headerSearchAddToCartProps} />
                  </div>
                </Fade>
              )}
            <ButtonBase
              className={clsx(classes.clearButton, {
                [classes.showClear]: inputValue && inputValue !== '',
              })}
              onClick={() => handleClearSelection()}
              aria-label={formatMessage({
                id: 'CLEAR_SEARCH',
                defaultMessage: 'Clear Search',
              })}
            >
              <XIcon fontSize="inherit" />
            </ButtonBase>
            <ButtonBase
              data-testid={id}
              id={id}
              className={classes.searchButton}
              aria-label={formatMessage({
                id: 'SEARCH',
                defaultMessage: 'Search',
              })}
              onClick={() => {
                handleSearch({
                  focusType,
                  searchValue,
                  dropdownLabel,
                })
                closeMenu()
              }}
            >
              <SearchIcon fontSize="inherit" />
            </ButtonBase>
          </>
        }
      />
    </div>
  )
}

export default HeaderSearchInput
